"use client";

import { useState, useEffect, useRef } from "react";

/**
 * 페이지네이션 상태를 URL 쿼리(?page=N)와 동기화하는 훅.
 *
 * - 마운트 시 URL 의 ?page 값을 초기 페이지로 복원한다.
 *   → 목록에서 상세로 진입한 뒤 뒤로가기 하면 보던 페이지가 그대로 유지된다.
 * - 페이지 변경 시 history.replaceState 로 URL 만 갱신한다.
 *   → 히스토리 항목이 늘지 않고(뒤로가기 한 번에 목록→이전화면), 스크롤 점프도 없다.
 * - page 가 1 이면 쿼리에서 제거해 URL 을 깔끔하게 유지한다.
 *
 * 반환값은 useState 와 동일한 [page, setPage] 시그니처라 기존 코드 교체가 쉽다.
 *
 * 주의: "검색/카테고리/정렬 변경 시 setPage(1)" 같은 리셋 effect 는 첫 마운트에
 * 실행되면 복원된 페이지를 덮어쓰므로, 사용처에서 첫 마운트 가드를 둬야 한다.
 *
 * 서버 렌더 단계에서 ?page 를 반영하려면 initialPage 를 넘긴다. 마운트 효과는
 * 브라우저에서만 돌기 때문에, 초기값 없이는 첫 HTML 이 늘 1 페이지가 된다.
 *
 * @param {string} [paramKey="page"] URL 쿼리 키
 * @param {number} [initialPage=1] 첫 렌더에 쓸 페이지 번호 (양의 정수만 인정)
 * @returns {[number, (updater: number | ((p: number) => number)) => void]}
 */
export function usePageParam(paramKey = "page", initialPage) {
  const initial = (() => {
    const n = Number(initialPage);
    return Number.isInteger(n) && n > 0 ? n : 1;
  })();
  const [page, setPage] = useState(initial);
  const restoredRef = useRef(false);

  // 마운트 시 URL 에서 초기 페이지 복원.
  //
  // 호출부가 이미 1 보다 큰 페이지를 정해 줬으면 그쪽이 단일 출처다 — 서버가
  // 정한 페이지를 여기서 다시 덮으면 두 곳이 같은 값을 다투게 된다.
  // 초기값이 없거나 1 이면(= 호출부가 주소를 못 읽었으면) 여기서 읽는다.
  useEffect(() => {
    if (initial > 1) {
      restoredRef.current = true;
      return;
    }
    try {
      const raw = new URLSearchParams(window.location.search).get(paramKey);
      const p = parseInt(raw || "1", 10);
      if (Number.isFinite(p) && p > 1) setPage(p);
    } catch {
      /* URL 파싱 실패 시 기본 1 페이지 유지 */
    }
    restoredRef.current = true;
    // 마운트 1회만 실행
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 페이지 변경 시 URL 동기화 (복원 완료 이후에만)
  useEffect(() => {
    if (!restoredRef.current) return;
    try {
      const url = new URL(window.location.href);
      if (page > 1) url.searchParams.set(paramKey, String(page));
      else url.searchParams.delete(paramKey);
      window.history.replaceState(window.history.state, "", url.toString());
    } catch {
      /* history 접근 불가 환경에서는 무시 */
    }
  }, [page, paramKey]);

  return [page, setPage];
}
